import { TransactionColumn } from "@common/types/views/transactionViews";
import { Ledger } from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    AMOUNT_COLUMN_SIZE,
    CATEGORY_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
    SCHEDULE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { SubaccountCell } from "@components/transactions/views/SubaccountCell";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ViewConfig } from "@lib/views";
import {
    buildLedgerAccountColumnIdByName,
    buildSuggestedLedgerColumnIdByName,
    uniqueLedgersByBasisAndName,
} from "@lib/ledgerViewColumns";
import { TransactionSortValue } from "../useSort";
import { AccrualActionCell } from "../../accounting/accrualReview/AccrualActionCell";
import { AccrualMatchCell } from "../../accounting/accrualReview/AccrualMatchCell";
import { AccrualReviewLedgerCell } from "../../accounting/accrualReview/AccrualReviewLedgerCell";
import { AccountCell } from "./AccountCell";
import { ReconciliationStatusCell } from "./ReconciliationStatusCell";
import { Exclude1099Cell } from "./Exclude1099Cell";
import { ReviewCell } from "./ReviewCell";
import { AmountCell } from "./AmountCell";
import { OriginalAmountCell } from "./OriginalAmountCell";
import { CategoryCell } from "./CategoryCell";
import { BankDescriptionCell } from "./BankDescriptionCell";
import { ClassesCell } from "./ClassesCell";
import { DateCell } from "./DateCell";
import { DescriptionCell } from "./DescriptionCell";
import { EntityCell } from "./EntityCell";
import { LockCell } from "./LockCell";
import { SplitCell } from "./SplitCell";
import { TransferMatchCell } from "./TransferMatchCell";
import { MatchingActionCell } from "./MatchingActionCell";
import { NoteCell } from "./NoteCell";
import { ReceiptsCell } from "./ReceiptsCell";
import { LedgerCell } from "./LedgerCell";
import { AccrualLedgerCell } from "./AccrualLedgerCell";
import { CounterpartyCell } from "./CounterpartyCell";
import { CustomerCell } from "./CustomerCell";
import { TransactionCTACell } from "./TransactionCTACell";
import { ScheduleCell } from "./ScheduleCell";
import { ScheduleReviewActionCell } from "@components/tasks/TaskBody/ScheduleReview/ScheduleReviewActionCell";
import { BillableExpenseInvoiceStatusCell } from "./BillableExpenseInvoiceStatusCell";

interface CreateTransactionsViewConfigParams {
    isAccrualLedgerEnabled: boolean;
    isGlFirst: boolean;
    isSchedulesEnabled: boolean;
    isClassesEnabled: boolean;
    isInvoicingEnabled: boolean;
    multiLedgerLedgers?: Ledger[];
}
export function createTransactionsViewConfig({
    isAccrualLedgerEnabled,
    isGlFirst,
    isSchedulesEnabled,
    isClassesEnabled,
    isInvoicingEnabled,
    multiLedgerLedgers = [],
}: CreateTransactionsViewConfigParams): ViewConfig<
    TransactionListItem,
    string,
    TransactionSortValue
> {
    const uniqueLedgers = uniqueLedgersByBasisAndName(multiLedgerLedgers);
    const uniqueAccrualLedgers = uniqueLedgers.filter(
        (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
    );
    const isMultiLedgerEnabled = uniqueLedgers.length > 0;
    const hasMultiAccrualLedgers = uniqueAccrualLedgers.length > 0;

    return {
        columns: {
            [TransactionColumn.DATE]: {
                accessorKey: "date",
                name: "Date",
                header: "Date",
                cell: DateCell,
                sort: {
                    asc: TransactionSortValue.DATE_ASC,
                    desc: TransactionSortValue.DATE_DESC,
                },
                immutable: true,
                ...DATE_COLUMN_SIZE,
            },
            [TransactionColumn.DESCRIPTION]: {
                accessorKey: "displayDescription",
                name: "Description",
                header: "Description",
                cell: DescriptionCell,
                sort: {
                    asc: TransactionSortValue.DESCRIPTION_ASC,
                    desc: TransactionSortValue.DESCRIPTION_DESC,
                },
                minSize: 200,
                maxSize: 600,
            },
            [TransactionColumn.BANK_DESCRIPTION]: {
                accessorKey: "bankDescription",
                name: "Bank description",
                header: "Bank description",
                cell: BankDescriptionCell,
                sort: {
                    asc: TransactionSortValue.BANK_DESCRIPTION_ASC,
                    desc: TransactionSortValue.BANK_DESCRIPTION_DESC,
                },
                minSize: 200,
                maxSize: 600,
            },
            // GL-first workspaces classify by general ledger account, so the
            // Category column is omitted entirely. Dropping the config entry
            // makes `buildColumns` skip it even for saved views and removes it
            // from the column settings picker.
            ...(isGlFirst
                ? {}
                : {
                      [TransactionColumn.CATEGORY]: {
                          accessorKey: "category.label",
                          name: "Category",
                          header: "Category",
                          headerAction: {
                              type: "variantMenu",
                              optionKey: "variant",
                              defaultValue: "full",
                              options: [
                                  { value: "full", label: "Full" },
                                  {
                                      value: "category-only",
                                      label: "Category only",
                                  },
                              ],
                              hiddenWhenValues: ["review"],
                          },
                          cell: CategoryCell,
                          sort: {
                              asc: TransactionSortValue.CATEGORY_ASC,
                              desc: TransactionSortValue.CATEGORY_DESC,
                          },
                          ...CATEGORY_COLUMN_SIZE,
                      },
                  }),
            [TransactionColumn.ACCOUNT]: {
                accessorKey: "financialAccount.name",
                name: "Account",
                header: "Account",
                cell: AccountCell,
                sort: {
                    asc: TransactionSortValue.ACCOUNT_ASC,
                    desc: TransactionSortValue.ACCOUNT_DESC,
                },
                minSize: 100,
                maxSize: 150,
            },
            [TransactionColumn.SUBACCOUNT]: {
                accessorKey: "subaccount.name",
                name: "Subaccount",
                header: "Subaccount",
                cell: SubaccountCell,
                minSize: 100,
                maxSize: 150,
            },
            [TransactionColumn.LEDGER]: {
                accessorKey: "journalEntryAccountId",
                name: "Cash ledger",
                header: "Cash ledger",
                cell: LedgerCell,
                sort: {
                    asc: TransactionSortValue.LEDGER_ASC,
                    desc: TransactionSortValue.LEDGER_DESC,
                },
                minSize: 200,
                maxSize: 400,
                internal: isMultiLedgerEnabled,
            },
            [TransactionColumn.ACCRUAL_LEDGER]: {
                accessorKey: "accrualAccountId",
                name: "Accrual ledger",
                header: "Accrual ledger",
                cell: AccrualLedgerCell,
                minSize: 200,
                maxSize: 400,
                internal: !isAccrualLedgerEnabled || isMultiLedgerEnabled,
            },
            ...Object.fromEntries(
                uniqueLedgers.map((ledger) => [
                    buildLedgerAccountColumnIdByName(ledger),
                    {
                        accessorKey: buildLedgerAccountColumnIdByName(ledger),
                        name: ledger.name,
                        header: ledger.name,
                        cell: LedgerCell,
                        minSize: 200,
                        maxSize: 400,
                    },
                ]),
            ),
            [TransactionColumn.AMOUNT]: {
                accessorKey: "amount",
                name: "Amount",
                header: "Amount",
                cell: AmountCell,
                sort: {
                    asc: TransactionSortValue.AMOUNT_ASC,
                    desc: TransactionSortValue.AMOUNT_DESC,
                },
                ...AMOUNT_COLUMN_SIZE,
            },
            ...(isSchedulesEnabled
                ? {
                      [TransactionColumn.SCHEDULE]: {
                          accessorKey: "schedule.scheduleName",
                          name: "Recognition schedule",
                          header: "Recognition schedule",
                          cell: ScheduleCell,
                          ...SCHEDULE_COLUMN_SIZE,
                      },
                      [TransactionColumn.SCHEDULE_REVIEW_ACTIONS]: {
                          accessorKey: "id",
                          name: "Action",
                          header: "Action",
                          cell: ScheduleReviewActionCell,
                          minSize: 180,
                          maxSize: 180,
                          internal: true,
                          immutable: true,
                          hiddenFromSettings: true,
                      },
                  }
                : {}),
            [TransactionColumn.ORIGINAL_AMOUNT]: {
                accessorKey: "originalAmount",
                name: "Original amount",
                header: "Amount",
                cell: OriginalAmountCell,
                sort: {
                    asc: TransactionSortValue.ORIGINAL_AMOUNT_ASC,
                    desc: TransactionSortValue.ORIGINAL_AMOUNT_DESC,
                },
                ...AMOUNT_COLUMN_SIZE,
                internal: true,
            },
            [TransactionColumn.COUNTERPARTY]: {
                accessorKey: "counterparty",
                name: "Counterparty",
                header: "Counterparty",
                cell: CounterpartyCell,
                sort: {
                    asc: TransactionSortValue.COUNTERPARTY_ASC,
                    desc: TransactionSortValue.COUNTERPARTY_DESC,
                },
                minSize: 200,
                maxSize: 400,
            },
            ...(isInvoicingEnabled
                ? {
                      [TransactionColumn.CUSTOMER]: {
                          accessorKey: "customer",
                          name: "Billable customer",
                          header: "Billable customer",
                          cell: CustomerCell,
                          sort: {
                              asc: TransactionSortValue.CUSTOMER_ASC,
                              desc: TransactionSortValue.CUSTOMER_DESC,
                          },
                          minSize: 200,
                          maxSize: 400,
                      },
                      [TransactionColumn.BILLABLE_EXPENSE_INVOICE_STATUS]: {
                          accessorKey: "billableExpenseInvoice.status",
                          name: "Billable invoice status",
                          header: "Billable invoice status",
                          cell: BillableExpenseInvoiceStatusCell,
                          sort: {
                              asc: TransactionSortValue.BILLABLE_EXPENSE_INVOICE_STATUS_ASC,
                              desc: TransactionSortValue.BILLABLE_EXPENSE_INVOICE_STATUS_DESC,
                          },
                          minSize: 160,
                          maxSize: 280,
                      },
                  }
                : {}),
            [TransactionColumn.NOTE]: {
                accessorKey: "memo",
                name: "Memo",
                header: "Memo",
                cell: NoteCell,
                minSize: 200,
                maxSize: 400,
            },
            [TransactionColumn.RECEIPTS]: {
                accessorKey: "receipts",
                name: "Receipt",
                header: "Receipt",
                cell: ReceiptsCell,
                minSize: 200,
                maxSize: 400,
            },
            [TransactionColumn.RECONCILIATION_STATUS]: {
                accessorKey: "reconciledInId",
                name: "Reconciliation status",
                header: "",
                cell: ReconciliationStatusCell,
                minSize: 40,
                maxSize: 40,
            },
            [TransactionColumn.EXCLUDE_FROM_1099]: {
                accessorKey: "excludeFrom1099",
                name: "Exclude from 1099",
                header: "Exclude from 1099",
                cell: Exclude1099Cell,
                minSize: 140,
                maxSize: 140,
            },
            [TransactionColumn.SPLIT]: {
                accessorKey: "splitParentId",
                name: "Split",
                header: "",
                cell: SplitCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 40,
                maxSize: 40,
            },
            [TransactionColumn.ENTITY]: {
                accessorKey: "entity",
                name: "Entity",
                header: "Entity",
                cell: EntityCell,
                minSize: 82,
                maxSize: 82,
            },
            ...(isClassesEnabled
                ? {
                      [TransactionColumn.CLASSES]: {
                          accessorKey: "classAssignments",
                          name: "Class",
                          header: "Class",
                          cell: ClassesCell,
                          sort: {
                              asc: TransactionSortValue.CLASS_ASC,
                              desc: TransactionSortValue.CLASS_DESC,
                          },
                          minSize: 100,
                          maxSize: 200,
                      },
                  }
                : {}),
            [TransactionColumn.REVIEW]: {
                accessorKey: "id",
                name: "Review",
                header: "Review",
                cell: ReviewCell,
                minSize: 60,
                maxSize: 60,
                sort: {
                    asc: TransactionSortValue.REVIEW_ASC,
                    desc: TransactionSortValue.REVIEW_DESC,
                },
            },
            [TransactionColumn.TRANSFER_MATCH]: {
                accessorKey: "pairedTransferId",
                name: "Match",
                header: "Match",
                cell: TransferMatchCell,
                minSize: 300,
                maxSize: 400,
            },
            [TransactionColumn.MATCHING_ACTION]: {
                accessorKey: "id",
                name: "Match Action",
                header: "",
                cell: MatchingActionCell,
                minSize: 120,
                maxSize: 120,
                immutable: true,
                internal: true,
            },
            [TransactionColumn.LOCK]: {
                accessorKey: "id",
                name: "Lock",
                header: "",
                cell: LockCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 32,
                maxSize: 32,
            },
            [TransactionColumn.CTA]: {
                accessorKey: "id",
                name: "Action",
                header: "",
                cell: TransactionCTACell,
                minSize: 140,
                maxSize: 140,
                internal: true,
            },
            [TransactionColumn.ACCRUAL_ACTIONS]: {
                accessorKey: "accrualAccountId",
                name: "Action",
                header: "Action",
                cell: AccrualActionCell,
                // Fixed width: needs to fit the "Confirm" (size=sm) button
                // plus the kebab icon-only button side-by-side without
                // wrapping. Keeping `minSize === maxSize` makes the column
                // non-flex so its rendered width can't drift from
                // TanStack's `columnSizing` state on container resize —
                // which would otherwise show as a gap between this column
                // and the "Suggested accrual ledger" column to its left.
                // See PRO-9320.
                minSize: 160,
                maxSize: 160,
                internal: true,
                immutable: true,
                hiddenFromSettings: true,
            },
            [TransactionColumn.ACCRUAL_MATCH]: {
                accessorKey: "accrualMatchSuggestions",
                name: "Suggested match",
                header: "Suggested match",
                cell: AccrualMatchCell,
                minSize: 280,
                maxSize: 400,
                internal: true,
                immutable: true,
            },
            [TransactionColumn.ACCRUAL_REVIEW_LEDGER]: {
                accessorKey: "accrualAccountId",
                name: "Suggested accrual ledger",
                header: "Suggested accrual ledger",
                cell: AccrualReviewLedgerCell,
                minSize: 200,
                maxSize: 400,
                internal: true,
                immutable: true,
                hiddenFromSettings: hasMultiAccrualLedgers,
            },
            ...Object.fromEntries(
                uniqueAccrualLedgers.map((ledger) => [
                    buildSuggestedLedgerColumnIdByName(ledger),
                    {
                        accessorKey: buildSuggestedLedgerColumnIdByName(ledger),
                        name: `Suggested ${ledger.name}`,
                        header: `Suggested ${ledger.name}`,
                        cell: AccrualReviewLedgerCell,
                        minSize: 200,
                        maxSize: 400,
                        internal: true,
                        immutable: true,
                    },
                ]),
            ),
        },
    };
}
